/**
 * CasingStringSection — ONE editable casing string, with ITS OWN torque window.
 *
 * ─── Why this is a component and not a loop body ────────────────────────────
 *
 * The same reason FieldCasingStringCard is one, arrived at from the other end.
 * There, `useTorqueProfile` is called directly and cannot go inside a `.map()`
 * because the hook count would change between renders. Here the hook is one
 * level down, inside `PipeTorqueFields`, so a `.map()` would in fact compile —
 * and the trap is the OTHER half of that bug.
 *
 * NewJobScreen resolved the torque inputs at the top of the screen:
 *
 *     const thread1Value = form.threadType === 'Other' ? ... : form.threadType;
 *     const size1Inches  = form.casingSizeInches === 'custom' ? ... : ...;
 *     const torqueSize1  = isTubingThread(thread1Value) ? canonicalTubingSize(...) : ...;
 *     // ...and the identical trio for 2
 *
 * Two parallel sets of variables, distinguished only by a trailing digit, feeding
 * two `<PipeTorqueFields>`. That is the shape that put string 1's makeup torque
 * on string 2's card on TNT J-4290 — 3,660 ft-lb optimum against a required
 * 6,680, under-torqued 1.8x on a P-110 coupling. With N strings there is no
 * `torqueSize3` to forget to add, and no way to pass the wrong one, because the
 * canonicalization happens HERE, against this component's own props.
 *
 * Keep it here. Hoisting it back to the parent is precisely how the second
 * section ends up keyed off the first string's thread again.
 *
 * ─── The cascades are per string too ────────────────────────────────────────
 *
 * Clearing the weight when the size changes, filling mm from inches, filling
 * kg/m from lb/ft: three effects that were written twice in the screen and are
 * written once here. They skip their first run so that hydrated values survive
 * mount — a custom size keeps the mm the user typed rather than having
 * 25.4 x inches recomputed over it.
 */

import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { PipeTorqueFields } from './PipeTorqueFields';
import ConnectionTypeSelect from '../ConnectionTypeSelect';
import {
  CASING_SIZES,
  TUBING_SIZES,
  TUBING_GRADES,
  canonicalTorqueSize,
  canonicalTubingSize,
  isTubingThread,
} from '../../data/casingData';
import {
  CASING_GRADES,
  MAX_CASING_TEXT_LENGTH,
  weightOptionsFor,
  type CasingStringForm,
} from '../../lib/casing-strings-form';

// Mirrors the same two constants in NewJobScreen. Presentation only.
const inputClass =
  'w-full bg-fieldiq-card border border-fieldiq-border rounded-lg px-4 py-3 text-white focus:border-fieldiq-accent focus:outline-none';
const labelClass = 'block text-sm text-white mb-1';

interface CasingStringSectionProps {
  value: CasingStringForm;
  /** 1-based ordinal, for the heading. Derived from position, like the payload. */
  stringNumber: number;
  /** Merged into this string's state by the parent. */
  onChange: (patch: Partial<CasingStringForm>) => void;
  /**
   * Absent on string 1. A job with no strings is not a valid casing job, so the
   * first section has no Remove control at all rather than a disabled one.
   */
  onRemove?: () => void;
}

export default function CasingStringSection({
  value: s,
  stringNumber,
  onChange,
  onRemove,
}: CasingStringSectionProps) {
  const sizeTable = s.pipeType === 'tubing' ? TUBING_SIZES : CASING_SIZES;
  const gradeTable = s.pipeType === 'tubing' ? TUBING_GRADES : CASING_GRADES;
  const availableWeights = weightOptionsFor(s.pipeType, s.sizeInches);

  // ─── Torque inputs, resolved against THIS string ──────────────────────────
  // Size canonicalization dispatches on the thread (isTubingThread), exactly
  // like the backend, so a tubing thread yields the tubing size key ("2-7/8"")
  // that lookupTubingSpec expects.
  const threadValue = s.threadType === 'Other' ? s.threadTypeOther : s.threadType;
  const resolvedSizeInches = s.sizeInches === 'custom' ? s.customSizeInches : s.sizeInches;
  const torqueSize = isTubingThread(threadValue)
    ? canonicalTubingSize(resolvedSizeInches)
    : canonicalTorqueSize(resolvedSizeInches);
  const rawWeight = s.sizeInches === 'custom' || s.weightLbFt === 'custom'
    ? s.customWeight
    : s.weightLbFt;
  const parsedWeight = rawWeight ? parseFloat(rawWeight) : NaN;

  // ─── Cascades ─────────────────────────────────────────────────────────────
  // `mounted` replaces the screen-wide isDuplicatingRef these effects used to
  // consult. Local is better: the ref was shared by both strings and by the
  // duplicate flow, so whether a hydrated value survived depended on the order
  // three unrelated code paths happened to run in.
  //
  // ONE REF PER EFFECT, deliberately. A single shared `mounted` flag does not
  // work: effects run in order, so the first effect would set it and the second
  // would then see `true` on the very first render and NOT skip — overwriting
  // the hydrated kg/m with the catalog's value before the user has touched
  // anything. `job_casing_strings` holds 69.94 where the catalog says 69.95,
  // and that save would have rewritten it silently.
  const sizeMounted = useRef(false);
  const weightMounted = useRef(false);
  const prevSize = useRef(s.sizeInches);
  useEffect(() => {
    if (!sizeMounted.current) { sizeMounted.current = true; prevSize.current = s.sizeInches; return; }

    // Weight options are size-specific, so a size change invalidates the weight.
    if (prevSize.current !== s.sizeInches) {
      prevSize.current = s.sizeInches;
      if (s.weightLbFt || s.weightKgM || s.customWeight) {
        onChange({ weightLbFt: '', weightKgM: '', customWeight: '' });
      }
    }

    if (s.sizeInches && s.sizeInches !== 'custom') {
      const match = sizeTable.find((t) => t.inches === s.sizeInches);
      if (match && match.mm !== s.sizeMm) onChange({ sizeMm: match.mm });
    } else if (s.sizeInches === 'custom' && s.customSizeInches) {
      const n = Number(s.customSizeInches);
      if (Number.isFinite(n) && n > 0) {
        const mm = String(Math.round(n * 25.4 * 100) / 100);
        if (mm !== s.customSizeMm) onChange({ customSizeMm: mm });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.sizeInches, s.customSizeInches, s.pipeType]);

  useEffect(() => {
    if (!weightMounted.current) { weightMounted.current = true; return; }
    if (s.weightLbFt && s.weightLbFt !== 'custom') {
      const match = availableWeights.find((w) => w.lbFt === s.weightLbFt);
      if (match && match.kgM !== s.weightKgM) onChange({ weightKgM: match.kgM });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.weightLbFt, s.sizeInches, s.pipeType]);

  /**
   * Switching casing <-> tubing clears this string's size/weight/grade/thread.
   * The catalogs do not overlap, so a stale casing value left behind would feed
   * a tubing torque lookup (or the reverse) and quietly resolve to the wrong
   * profile rather than to nothing.
   */
  const setPipeType = (pt: string) =>
    onChange({
      pipeType: pt as 'casing' | 'tubing',
      sizeInches: '', sizeMm: '', weightLbFt: '', weightKgM: '',
      grade: '', threadType: '', threadTypeOther: '',
      customSizeInches: '', customSizeMm: '', customWeight: '', customGrade: '',
    });

  return (
    <div className="border border-fieldiq-border rounded-lg px-4 py-4 space-y-4 bg-fieldiq-dark">
      <div className="flex items-center justify-between">
        <span className="text-sm text-fieldiq-accent font-medium">String #{stringNumber}</span>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-fieldiq-danger text-xs hover:underline flex items-center gap-1"
          >
            <X size={14} /> Remove
          </button>
        )}
      </div>

      {/* Pipe Type */}
      <div>
        <label className={labelClass}>Pipe Type</label>
        <div className="flex rounded-lg border border-fieldiq-border overflow-hidden">
          {[{ value: 'casing', label: 'Casing' }, { value: 'tubing', label: 'Tubing' }].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setPipeType(opt.value)}
              className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                s.pipeType === opt.value
                  ? 'bg-fieldiq-accent text-fieldiq-dark'
                  : 'bg-fieldiq-card text-white hover:text-white'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/*
        Size / Weight — the four measurement fields.

        No `maxLength` on these: they are `type="number"`, and the attribute is
        ignored on a number input. Their 20-character bound (the width of
        `job_casing_strings.size_inches` and its siblings) is enforced by
        findCasingStringViolation on save, which names the string and the field.
        A decorative maxLength here would read as a guard that is not there.
      */}
      {/* Size */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClass}>Size (Imperial)</label>
          <select
            className={inputClass}
            value={s.sizeInches}
            onChange={(e) => onChange({ sizeInches: e.target.value })}
          >
            <option value="">-- Select Size --</option>
            {sizeTable.map((t) => (
              <option key={t.inches} value={t.inches}>{t.label} ({t.inches}")</option>
            ))}
            <option value="custom">+ Custom</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Size (mm)</label>
          {s.sizeInches === 'custom' ? (
            <input
              className={inputClass}
              type="number"
              step="0.01"
              placeholder="mm"
              value={s.customSizeMm}
              onChange={(e) => onChange({ customSizeMm: e.target.value })}
            />
          ) : (
            <input className={`${inputClass} opacity-60`} value={s.sizeMm} readOnly tabIndex={-1} />
          )}
        </div>
      </div>
      {s.sizeInches === 'custom' && (
        <div>
          <label className={labelClass}>Custom Size (inches)</label>
          <input
            className={inputClass}
            type="number"
            step="0.001"
            placeholder="e.g. 6.625"
            value={s.customSizeInches}
            onChange={(e) => onChange({ customSizeInches: e.target.value })}
          />
        </div>
      )}

      {/* Weight */}
      <div>
        <label className={labelClass}>Weight</label>
        {!s.sizeInches ? (
          <p className="text-sm text-fieldiq-muted italic">Select a size first</p>
        ) : s.sizeInches === 'custom' ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <input
                className={inputClass}
                type="number"
                step="0.01"
                placeholder="lb/ft"
                  value={s.customWeight}
                onChange={(e) => onChange({ customWeight: e.target.value })}
              />
            </div>
            <div>
              <input
                className={`${inputClass} opacity-60`}
                value={s.customWeight ? (parseFloat(s.customWeight) * 1.4882).toFixed(2) : ''}
                readOnly
                tabIndex={-1}
                placeholder="kg/m (auto)"
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <select
                className={inputClass}
                value={s.weightLbFt}
                onChange={(e) => onChange({ weightLbFt: e.target.value })}
              >
                <option value="">-- Select Weight --</option>
                {availableWeights.map((w) => (
                  <option key={w.lbFt} value={w.lbFt}>{w.label}</option>
                ))}
                <option value="custom">+ Custom</option>
              </select>
            </div>
            <div>
              {s.weightLbFt === 'custom' ? (
                <input
                  className={inputClass}
                  type="number"
                  step="0.01"
                  placeholder="lb/ft (custom)"
                      value={s.customWeight}
                  onChange={(e) => onChange({ customWeight: e.target.value })}
                />
              ) : (
                <input
                  className={`${inputClass} opacity-60`}
                  value={s.weightKgM}
                  readOnly
                  tabIndex={-1}
                  placeholder="kg/m (auto)"
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Grade */}
      <div>
        <label className={labelClass}>Grade</label>
        <select
          className={inputClass}
          value={s.grade}
          onChange={(e) => onChange({ grade: e.target.value })}
        >
          <option value="">-- Select Grade --</option>
          {gradeTable.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
          <option value="custom">+ Custom</option>
        </select>
      </div>
      {s.grade === 'custom' && (
        <div>
          <label className={labelClass}>Custom Grade</label>
          <input
            className={inputClass}
            placeholder="Enter grade"
            maxLength={MAX_CASING_TEXT_LENGTH}
            value={s.customGrade}
            onChange={(e) => onChange({ customGrade: e.target.value })}
          />
        </div>
      )}

      {/* Thread Type */}
      <div>
        <label className={labelClass}>Thread Type</label>
        <ConnectionTypeSelect
          value={threadValue}
          onChange={(v) => onChange({ threadType: v })}
          pipeType={s.pipeType}
        />
      </div>

      {/*
        Makeup torque, read-only, auto-filled from the API RP 5C1 spec or a
        saved profile. `onChange` is deliberately empty: the values are keyed by
        size/weight/grade/connection in `torque_profiles` and recalled from
        there, and `jobs` has no torque columns for the form to carry. The
        pre-array screen merged string 1's values into form state and never sent
        them anywhere, which read as though they were being saved.
      */}
      <PipeTorqueFields
        size={torqueSize}
        weightPpf={Number.isFinite(parsedWeight) ? parsedWeight : null}
        grade={(s.grade === 'custom' ? s.customGrade : s.grade) as any}
        connectionType={threadValue as any}
        onChange={() => {}}
      />
    </div>
  );
}
